(function () {
  'use strict';

  if (customElements.get('youtube-videos')) return;

  const VIDEO_ID_PATTERN = /^[\w-]{11}$/;
  const PLAY_ICON =
    '<svg viewBox="0 0 68 48" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true"><path d="M66.52 7.74a8.34 8.34 0 0 0-5.86-5.9C55.49.13 34 .13 34 .13s-21.49 0-26.66 1.71a8.34 8.34 0 0 0-5.86 5.9A87.07 87.07 0 0 0 0 24a87.07 87.07 0 0 0 1.48 16.26 8.34 8.34 0 0 0 5.86 5.9c5.17 1.71 26.66 1.71 26.66 1.71s21.49 0 26.66-1.71a8.34 8.34 0 0 0 5.86-5.9A87.07 87.07 0 0 0 68 24a87.07 87.07 0 0 0-1.48-16.26z" fill="currentColor"></path><path d="M27 34.5 44.5 24 27 13.5v21z" fill="#fff"></path></svg>';

  customElements.define(
    'youtube-videos',
    class extends HTMLElement {
      connectedCallback() {
        this.apiKey = this.getAttribute('data-api-key');
        this.channelId = this.getAttribute('data-channel-id');
        this.limit = parseInt(this.getAttribute('data-limit'), 10) || 8;
        this.cacheHours = parseFloat(this.getAttribute('data-cache-hours')) || 24;
        this.clickAction = this.getAttribute('data-click-action') || 'inline';
        this.hideShorts = this.hasAttribute('data-hide-shorts');
        this.minDuration = (parseFloat(this.getAttribute('data-min-minutes')) || 0) * 60;
        this.grid = this.querySelector('[data-videos-grid]');

        if (!this.grid || !this.apiKey || !this.channelId) return;

        this.load();
      }

      get cacheKey() {
        return (
          'youtube-videos-' +
          this.channelId +
          '-' +
          this.limit +
          (this.hideShorts ? '-noshorts' : '') +
          (this.minDuration ? '-min' + this.minDuration : '')
        );
      }

      readCache() {
        try {
          const cached = JSON.parse(localStorage.getItem(this.cacheKey));
          if (!cached || !Array.isArray(cached.videos) || !cached.videos.length) return null;
          if (Date.now() - cached.time > this.cacheHours * 3600000) return null;
          return cached.videos;
        } catch (error) {
          return null;
        }
      }

      writeCache(videos) {
        try {
          localStorage.setItem(this.cacheKey, JSON.stringify({time: Date.now(), videos: videos}));
        } catch (error) {
          // Storage unavailable or full — the next page load fetches again
        }
      }

      load() {
        const cached = this.readCache();

        if (cached) {
          this.render(cached);
          return;
        }

        let request = this.hideShorts
          ? this.fetchVideos('UULF').catch(() => this.fetchVideos('UU'))
          : this.fetchVideos('UU');

        request
          .then((videos) => {
            this.writeCache(videos);
            this.render(videos);
          })
          .catch(() => {
            if (this.hasAttribute('data-design-mode')) return;
            const section = this.closest('.shopify-section') || this;
            section.style.display = 'none';
          });
      }

      fetchVideos(playlistPrefix) {
        const uploadsPlaylist = playlistPrefix + this.channelId.slice(2);
        const maxResults = this.minDuration ? Math.min(50, this.limit * 2 + 5) : this.limit;
        const url =
          'https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=' +
          maxResults +
          '&playlistId=' +
          encodeURIComponent(uploadsPlaylist) +
          '&key=' +
          encodeURIComponent(this.apiKey);

        return this.fetchJson(url)
          .then((json) => {
            const videos = (json.items || [])
              .map((item) => {
                const snippet = item.snippet || {};
                const resource = snippet.resourceId || {};
                return {id: resource.videoId || '', title: snippet.title || ''};
              })
              .filter((video) => VIDEO_ID_PATTERN.test(video.id));

            if (!videos.length) throw new Error('No videos returned');
            return this.filterByDuration(videos);
          })
          .then((videos) => {
            if (!videos.length) throw new Error('No videos returned');
            return videos.slice(0, this.limit);
          });
      }

      filterByDuration(videos) {
        if (!this.minDuration) return Promise.resolve(videos);

        const ids = videos.map((video) => video.id).join(',');
        const url =
          'https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=' +
          ids +
          '&key=' +
          encodeURIComponent(this.apiKey);

        return this.fetchJson(url).then((json) => {
          const durations = {};
          (json.items || []).forEach((item) => {
            durations[item.id] = this.parseDuration((item.contentDetails || {}).duration);
          });

          return videos.filter((video) => durations[video.id] >= this.minDuration);
        });
      }

      parseDuration(duration) {
        const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(duration || '');
        if (!match) return 0;
        return (parseInt(match[1] || 0, 10) * 3600) + (parseInt(match[2] || 0, 10) * 60) + parseInt(match[3] || 0, 10);
      }

      fetchJson(url) {
        return fetch(url).then((response) => {
          if (!response.ok) throw new Error('YouTube API responded with ' + response.status);
          return response.json();
        });
      }

      render(videos) {
        this.grid.innerHTML = '';

        videos.forEach((video) => {
          this.grid.appendChild(this.buildCard(video));
        });

        const slider = this.querySelector('grid-slider');
        if (slider) slider.dispatchEvent(new CustomEvent('theme:grid-slider:init'));
      }

      buildCard(video) {
        const item = document.createElement('div');
        item.className = 'grid-item youtube-video';
        item.setAttribute('data-grid-item', '');

        let media;
        if (this.clickAction === 'link') {
          media = document.createElement('a');
          media.href = 'https://www.youtube.com/watch?v=' + video.id;
          media.target = '_blank';
          media.rel = 'noopener';
        } else {
          media = document.createElement('button');
          media.type = 'button';
          media.addEventListener('click', () => this.play(media, video), {once: true});
        }
        media.className = 'youtube-video__media';
        media.setAttribute('aria-label', video.title);

        const image = document.createElement('img');
        image.src = 'https://i.ytimg.com/vi/' + video.id + '/hqdefault.jpg';
        image.alt = video.title;
        image.loading = 'lazy';
        media.appendChild(image);

        const play = document.createElement('span');
        play.className = 'youtube-video__play';
        play.innerHTML = PLAY_ICON;
        media.appendChild(play);

        const title = document.createElement('p');
        title.className = 'youtube-video__title';
        title.textContent = video.title;

        item.appendChild(media);
        item.appendChild(title);
        return item;
      }

      play(media, video) {
        const player = document.createElement('div');
        player.className = 'youtube-video__media youtube-video__media--playing';

        const iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + video.id + '?autoplay=1&rel=0';
        iframe.title = video.title;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        player.appendChild(iframe);

        media.replaceWith(player);
      }
    }
  );
})();
